import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { useApi } from "../service/ApiContext";
import { LocalStorage } from "../utils/Utils";

const ONE_MB = 1024 * 1024;

interface PDFStreamViewerProps {
  showDownload?: boolean | null;
  artifact_Id?: string;
  onReachEnd?: (artifactId: string, pageNumber: number) => void;
  autoLoad?: boolean;
  resetKey?: number;
  children?: React.ReactNode;
}

export interface PDFStreamViewerHandle {
  resetToFirstPage: () => void;
  reloadPDF: () => Promise<void>;
  getCurrentPage: () => number;
  getTotalPages: () => number;
  hasReachedEnd: () => boolean; // Add this
}

const PDFStreamViewer = forwardRef<PDFStreamViewerHandle, PDFStreamViewerProps>(
  (
    {
      showDownload = false,
      artifact_Id = "",
      onReachEnd = undefined,
      autoLoad = true,
      resetKey = 0,
      children,
    },
    ref: any,
  ) => {
    const [artifactId, setArtifactId] = useState(artifact_Id);
    const [pdfUrl, setPdfUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(0); // Changed to 0-based for consistency
    const [totalPages, setTotalPages] = useState(0);
    const [internalResetKey, setInternalResetKey] = useState(0);
    const [hasReachedEnd, setHasReachedEnd] = useState(false); // Track if user has reached end

    // Use ref for triggered state to avoid closure issues
    const hasTriggeredEndRef = useRef(false);
    const api = useApi();
    const abortRef = useRef<any>(null);
    const viewerRef = useRef<any>(null);

    // Store plugin instances
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
      sidebarTabs: () => [],
      renderToolbar: (Toolbar) => (
        <Toolbar>
          {(slots) => {
            const {
              GoToPreviousPage,
              GoToNextPage,
              CurrentPageInput,
              NumberOfPages,
              ZoomOut,
              ZoomIn,
              EnterFullScreen,
              Download,
            } = slots;

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  borderBottom: "1px solid #ddd",
                  background: "#fafafa",
                  gap: 8,
                  width: "100%",
                }}
              >
                <GoToPreviousPage />
                <CurrentPageInput />
                <span>/</span>
                <NumberOfPages />
                <GoToNextPage />
                <div
                  style={{
                    marginLeft: "25%",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <ZoomOut />
                  <ZoomIn />
                </div>
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  {showDownload && <Download />}
                  <EnterFullScreen />
                </div>
              </div>
            );
          }}
        </Toolbar>
      ),
    });

    // Page navigation plugin
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { jumpToPage } = pageNavigationPluginInstance;

    // Reset function
    const resetToFirstPage = useCallback(() => {
      console.log("Resetting PDF to first page");

      // Reset state
      setCurrentPage(0);
      setHasReachedEnd(false);
      hasTriggeredEndRef.current = false;

      // Try to navigate to first page using the plugin
      try {
        if (jumpToPage && typeof jumpToPage === "function") {
          jumpToPage(0); // 0-based index
          console.log("Programmatically jumped to first page");
        }
      } catch (err) {
        console.log("Could not navigate programmatically, trying scroll:", err);
      }

      // Scroll to top as fallback
      setTimeout(() => {
        const viewerContainer = document.querySelector(".rpv-core__viewer");
        if (viewerContainer) {
          viewerContainer.scrollTop = 0;
        }
      }, 100);

      // Increment internal reset key to force some re-renders if needed
      setInternalResetKey((prev) => prev + 1);
    }, [jumpToPage]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      resetToFirstPage,
      reloadPDF: loadPdf,
      getCurrentPage: () => currentPage + 1, // Return 1-based for external use
      getTotalPages: () => totalPages,
      hasReachedEnd: () => hasReachedEnd, // Expose hasReachedEnd
    }));

    // Handle external resetKey changes
    useEffect(() => {
      if (resetKey > 0) {
        console.log("External reset triggered via key");
        resetToFirstPage();
      }
    }, [resetKey, resetToFirstPage]);

    // 🔐 base64 → Uint8Array
    const base64ToBytes = (base64: any) => {
      const binary = atob(base64.replace(/\s/g, ""));
      return Uint8Array.from(binary, (c) => c.charCodeAt(0));
    };

    // 📦 Small PDF
    const fetchFullPdf = async (streamUrl: any) => {
      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      const res = await api.get<any>(streamUrl);
      if (!res.data?.base64_content) {
        throw new Error("Missing base64_content");
      }
      return new Blob([base64ToBytes(res.data.base64_content)], {
        type: "application/pdf",
      });
    };

    // 📦 Large PDF (range)
    const fetchPdfInChunks = async (streamUrl: any, fileSize: any) => {
      const chunks = [];
      let start = 0;
      let isComplete = false;
      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      try {
        while (start < fileSize && !isComplete) {
          const end = Math.min(start + ONE_MB - 1, fileSize - 1);

          const chunkAbortController = new AbortController();
          abortRef.current = chunkAbortController;

          // Pass range as query parameter
          const res = await api.get<any>(streamUrl, {
            file_range: `bytes=${start}-${end}`,
          });

          if (!res.data?.base64_content) {
            throw new Error("Missing base64_content in chunk");
          }

          chunks.push(base64ToBytes(res.data.base64_content));

          if (res.data.is_complete !== undefined) {
            isComplete = res.data.is_complete;
          }

          if (res.data.end_offset !== undefined) {
            start = res.data.end_offset + 1;
          } else {
            start = end + 1;
          }
        }

        return new Blob(chunks, { type: "application/pdf" });
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("Fetch aborted");
          throw err;
        }
        throw new Error(`Failed to fetch PDF chunks: ${err.message}`);
      }
    };

    const loadPdf = async () => {
      if (!artifactId) return;

      setLoading(true);
      setError("");
      setPdfUrl("");
      setHasReachedEnd(false); // Reset end state
      hasTriggeredEndRef.current = false;
      setCurrentPage(0);
      setTotalPages(0);

      abortRef.current?.abort();
      abortRef.current = new AbortController();
      api.setAuthToken(LocalStorage.getLocalData("access_token"));
      try {
        const sessionRes = await api.post<any>(
          `/create_stream_session?file_id=${artifactId}`,
        );

        const { file_size, session_id } = sessionRes.data;

        const pdfBlob =
          file_size < ONE_MB
            ? await fetchFullPdf(`/stream_pdf/${session_id}`)
            : await fetchPdfInChunks(`/stream_pdf/${session_id}`, file_size);

        setPdfUrl(URL.createObjectURL(pdfBlob));
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err.message || "Failed to load PDF");
        }
      } finally {
        setLoading(false);
      }
    };

    // ✅ Improved Handler for page change events
    const handlePageChange = useCallback(
      (e: any) => {
        if (!e.currentPage || !e.doc) return;

        const newPage = e.currentPage; // Keep as 0-based
        const total = e.doc.numPages || 0;

        setCurrentPage(newPage);
        setTotalPages(total);

        console.log("PDF Page changed:", {
          currentPage: newPage + 1, // Log as 1-based for readability
          totalPages: total,
          hasTriggeredEnd: hasTriggeredEndRef.current,
        });

        // Check if we've reached the last page
        // For single-page PDFs (total === 1), we reach end when page 0 loads
        // For multi-page PDFs, we reach end when on last page (newPage === total - 1)
        const isLastPage =
          total > 0 &&
          ((total === 1 && newPage === 0) || // Single page PDF
            (total > 1 && newPage === total - 1)); // Multi-page PDF

        if (isLastPage) {
          setHasReachedEnd(true);

          // Only trigger callback once
          if (!hasTriggeredEndRef.current) {
            console.log("✅ Reached last page of PDF - Triggering callback");
            hasTriggeredEndRef.current = true;

            // Call the parent's callback function
            if (onReachEnd && typeof onReachEnd === "function") {
              // Pass 1-based page number to parent
              onReachEnd(artifactId, newPage + 1);
            }
          }
        } else {
          setHasReachedEnd(false);
        }
      },
      [artifactId, onReachEnd],
    );

    // Handle document load to capture total pages for single-page PDFs
    const handleDocumentLoad = useCallback(
      (e: any) => {
        const total = e.doc.numPages || 0;
        setTotalPages(total);

        console.log("Document loaded, total pages:", total);

        // For single-page PDFs, immediately mark as reached end
        if (total === 1) {
          setHasReachedEnd(true);

          if (!hasTriggeredEndRef.current) {
            console.log("✅ Single-page PDF loaded - marking as reached end");
            hasTriggeredEndRef.current = true;

            if (onReachEnd && typeof onReachEnd === "function") {
              onReachEnd(artifactId, 1);
            }
          }
        }
      },
      [artifactId, onReachEnd],
    );

    useEffect(() => {
      return () => {
        abortRef.current?.abort();
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      };
    }, [pdfUrl]);

    useEffect(() => {
      if (artifact_Id && autoLoad) {
        setArtifactId(artifact_Id);
        loadPdf();
      }
    }, [artifact_Id]);

    return (
      <div
        key={`pdf-viewer-${internalResetKey}`}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              background: "rgba(255,255,255,0.8)",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            Loading PDF...
          </div>
        )}

        {/* Render children when user has reached the end */}
        {children && hasReachedEnd && (
          <div
            style={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              padding: "10px 20px",
              borderRadius: "8px",
            }}
          >
            {children}
          </div>
        )}

        {pdfUrl && (
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              fileUrl={pdfUrl}
              plugins={[
                defaultLayoutPluginInstance,
                pageNavigationPluginInstance,
              ]}
              onPageChange={handlePageChange}
              onDocumentLoad={handleDocumentLoad}
            />
          </Worker>
        )}
      </div>
    );
  },
);

PDFStreamViewer.displayName = "PDFStreamViewer";
export default PDFStreamViewer;
